import React, { useState, useRef } from 'react';
import { WhiteboardSource } from '../types';
import { uploadWhiteboardImage, clearWhiteboardSource } from '../services/whiteboard';

interface WhiteboardUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  currentPhase: string;
  sources: WhiteboardSource[];
  onUploadSuccess: (result: {
    extractedInfo: string;
    sourceId: string;
    sourceName: string;
    timestamp: string;
    isNewInfo: boolean;
  }) => void;
  onSourceCleared: (sourceId: string) => void;
}

export function WhiteboardUploadModal({
  isOpen,
  onClose,
  sessionId,
  currentPhase,
  sources,
  onUploadSuccess,
  onSourceCleared
}: WhiteboardUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [sourceName, setSourceName] = useState('');
  const [diffMode, setDiffMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      setError('Please select a JPEG or PNG image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image too large. Maximum size is 10MB');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Auto-generate source name if empty
    if (!sourceName) {
      const timestamp = new Date().toLocaleTimeString();
      setSourceName(`Whiteboard ${timestamp}`);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !sourceName.trim()) {
      setError('Please select an image and enter a source name');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadResult('');

    try {
      // Generate unique source ID
      const sourceId = `source_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const result = await uploadWhiteboardImage({
        sessionId,
        sourceId,
        sourceName: sourceName.trim(),
        imageBase64: previewUrl,
        diffMode,
        currentPhase
      });

      setUploadResult(result.extractedInfo);

      // Notify parent
      onUploadSuccess({
        ...result,
        sourceName: sourceName.trim()
      });

      // Clear form
      setSelectedFile(null);
      setPreviewUrl('');
      setSourceName('');

    } catch (err: any) {
      setError(err.message || 'Failed to upload whiteboard image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearSource = async (sourceId: string) => {
    if (!window.confirm('Clear history for this whiteboard source?')) return;

    try {
      await clearWhiteboardSource(sessionId, sourceId);
      onSourceCleared(sourceId);
    } catch (err: any) {
      alert(`Failed to clear source: ${err.message}`);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '15px',
          padding: '30px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: '20px', fontSize: '1.5rem', color: '#052831' }}>
          🖼️ Upload Whiteboard Image
        </h2>

        {/* File Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#052831' }}>
            Select Image
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileSelect}
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #00c6c2',
              borderRadius: '8px',
              fontSize: '0.9rem'
            }}
          />
        </div>

        {/* Preview */}
        {previewUrl && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#052831' }}>
              Preview
            </label>
            <img
              src={previewUrl}
              alt="Whiteboard preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                border: '2px solid #00c6c2',
                borderRadius: '8px',
                objectFit: 'contain'
              }}
            />
          </div>
        )}

        {/* Source Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#052831' }}>
            Source Name
          </label>
          <input
            type="text"
            value={sourceName}
            onChange={(e) => setSourceName(e.target.value)}
            placeholder="e.g., Main Whiteboard, Design Board"
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #00c6c2',
              borderRadius: '8px',
              fontSize: '0.9rem',
              color: '#052831'
            }}
          />
        </div>

        {/* Diff Mode Toggle */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={diffMode}
              onChange={(e) => setDiffMode(e.target.checked)}
              style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: 'bold', color: '#052831' }}>
              Diff Mode (Extract only new changes)
            </span>
          </label>
          <p style={{ marginTop: '5px', fontSize: '0.85rem', color: '#666', marginLeft: '28px' }}>
            Enable this for incremental whiteboard updates to avoid duplicating information
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px',
              background: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '20px',
              fontSize: '0.9rem'
            }}
          >
            {error}
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <div
            style={{
              padding: '15px',
              background: '#f0fdf4',
              border: '2px solid #10b981',
              borderRadius: '8px',
              marginBottom: '20px'
            }}
          >
            <h4 style={{ marginBottom: '10px', color: '#052831' }}>Extracted Information:</h4>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#052831', maxHeight: '200px', overflowY: 'auto' }}>
              {uploadResult}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !sourceName.trim()}
            style={{
              flex: 1,
              padding: '14px',
              background: isUploading || !selectedFile || !sourceName.trim() ? '#9ca3af' : '#00c6c2',
              border: 'none',
              borderRadius: '8px',
              color: isUploading || !selectedFile || !sourceName.trim() ? '#6b7280' : '#052831',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: isUploading || !selectedFile || !sourceName.trim() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isUploading ? 'Uploading & Analyzing...' : 'Upload & Analyze'}
          </button>

          <button
            onClick={onClose}
            style={{
              padding: '14px 24px',
              background: '#e5e7eb',
              border: 'none',
              borderRadius: '8px',
              color: '#052831',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Close
          </button>
        </div>

        {/* Existing Sources */}
        {sources.length > 0 && (
          <div>
            <h3 style={{ marginBottom: '10px', fontSize: '1.1rem', color: '#052831' }}>
              Existing Sources
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {sources.map((source) => (
                <div
                  key={source.sourceId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#052831' }}>
                      {source.sourceName}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      Last upload: {new Date(source.lastUpload).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleClearSource(source.sourceId)}
                    style={{
                      padding: '6px 12px',
                      background: '#ef4444',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
